import { RefreshCw } from 'lucide-react';
import type { DbHealthReport } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useApiResource } from '#client/hooks/useResourceItem';
import { useMutation } from '#client/hooks/useMutation';
import { formatDateTime } from '#client/lib/format';
import { ENDPOINT } from '#client/lib/endpoints';
import { useToast } from '#client/providers/ToastProvider';
import { HealthOverview, StorageCards, WorkloadCards } from './DatabaseHealthSections';
import { analyzeDatabase } from './settings.service';

const TITLE = 'Database health';

/**
 * How the database is doing, for the admin.
 *
 * Read from Postgres's own statistics at request time, plus the request
 * latency this server process has recorded. The checks at the top are the
 * page's alerts: nothing here pages anyone, so a critical row is the signal.
 */
export function DatabaseHealthPage() {
  const toast = useToast();
  const request = useApiResource<DbHealthReport>(ENDPOINT.dbHealth);
  const analyzeRequest = useMutation();
  const analyzing = analyzeRequest.isPending;

  async function handleAnalyze() {
    const outcome = await analyzeRequest.run(analyzeDatabase);
    if (!outcome.ok) {
      toast.error(outcome.message);
      return;
    }
    toast.success('Statistics refreshed');
    request.refresh();
  }

  if (request.error) return <PageMessage title={TITLE}>{request.error}</PageMessage>;
  const report = request.data;
  if (!report) return <PageLoading title={TITLE} />;

  return (
    <Page
      title={TITLE}
      subtitle={`As of ${formatDateTime(report.generated_at)}`}
      primaryAction={
        <Button icon={<RefreshCw aria-hidden />} onClick={request.refresh}>
          Refresh
        </Button>
      }
      secondaryActions={
        <Button variant="ghost" onClick={() => void handleAnalyze()} disabled={analyzing}>
          {analyzing ? 'Refreshing statistics…' : 'Refresh statistics (ANALYZE)'}
        </Button>
      }
      fullWidth
    >
      <div className="c-form-stack">
        <HealthOverview report={report} />
        <WorkloadCards report={report} />
        <StorageCards report={report} />
      </div>
    </Page>
  );
}
